import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { handleError } from 'store/thunks/errorHandlerThunks';

import { listCatalogNdcsByProductIdApi, listCatalogProductsByDrugNameApi } from 'api/drug';
import { bulkOverrideLineItemsApi, bulkOverrideOperation, listBulkLabelsApi } from 'api/requests';

import {
  buildCommitChunks,
  isDefiniteRejection,
  submitChunksSequentially,
} from '../utils/bulkOverrideChunking';
import {
  EMPTY_FILTERS,
  buildPartnerFacet,
  isoDay,
  ndcDigits,
  samePartner,
  selectedAndVisible,
  standingFilterFor,
} from '../utils/bulkOverrideFilters';
import {
  EMPTY_ROW_EDIT,
  buildBulkStagedValue,
  commitItemsFor,
  isStagedChange,
} from '../utils/bulkOverrideStaging';
import useFillLaterSchedule from './useFillLaterSchedule';

/* ---------- state ---------- */

const DRUG_SEARCH_DEBOUNCE_MS = 250;
export const MIN_DRUG_SEARCH_LENGTH = 3;
// one fetch per drug; the screen is for a single drug's open orders, not a browse
/* One fetch covers the whole drug and every advanced filter runs against it, so this is a
 * safety valve rather than a page size. Zero means no cap; set the env var to put one back
 * if a payload ever starts to hurt. Send it explicitly either way -- omitting it would let
 * listBulkLabelsApi default to BULK_LABEL_PAGE_SIZE and quietly return one page. */
const MAX_ROWS_PER_DRUG = Number(process.env.REACT_APP_BULK_OVERRIDE_ROW_LIMIT) || 0;

/* The same window the single-order review actions allow -- showNeedsReviewButton in
 * PatientOrdersAccordion and OrderActionsMenu gate on exactly these three. Past verification an
 * order is not reviewable anywhere else in the product, so the bulk queue must not offer it. */

/* Review has no drug scope and no fulfilment status to narrow by, so on its own taggedForReview
 * listed orders that have already left -- shipped, in shipment, transferred, cancelled, and
 * anything past verification. Core refuses every one of those on commit
 * (INELIGIBLE_FOR_BULK_OVERRIDE_PROCESSING_STATES), so they were rows the operator could select
 * and never write. */
const useBulkOverride = (params) => {
  /* onQueued fires when the server put the work onto SQS instead of doing it in the request —
   * with the batch (or, for a client-chunked run, batches) it accepted. The dialog uses it to
   * start tracking realtime progress; without a handler the queued branch still returns cleanly,
   * it just has no progress bar to drive.
   *
   * onChunkedCommitStart fires before the FIRST chunk of a multi-chunk commit is submitted, with
   * every line item the run covers. The label tracker has to be listening from here: labels from
   * an early chunk regenerate while later chunks are still submitting, and socket events that
   * arrive before tracking starts are dropped, not replayed.
   *
   * onBatchAccepted fires per chunk the server queues, the moment its mutation resolves — the
   * override tracker registers each batch here, because a batch's first events precede its own
   * mutation response and a tracker armed after the last submit has already missed them. */
  const { open, operation, isFillLaterMove, onQueued, onChunkedCommitStart } = params;
  const { onBatchAccepted } = params;
  const isReview = operation === bulkOverrideOperation.NEEDS_REVIEW_UPDATE;
  const isProductChange = operation === bulkOverrideOperation.PRODUCT_ID_UPDATE;
  const isUnfulfillment = operation === bulkOverrideOperation.UNFULFILLMENT_UPDATE;
  /* Quantity mode reuses the default standing filter (PRODUCT_FULFILLMENT — not yet filled),
   * which is exactly the population core will accept: an unscanned line item has no lot
   * allocation yet, so changing its quantity cannot strand one. */
  const isQuantityChange = operation === bulkOverrideOperation.QUANTITY_UPDATE;
  const isFillLaterSplit = operation === bulkOverrideOperation.FILL_LATER_SPLIT && !isFillLaterMove;
  const dispatch = useDispatch();

  const [drugOptions, setDrugOptions] = useState([]);
  const [isDrugLoading, setIsDrugLoading] = useState(false);
  const [product, setProduct] = useState(null);
  const [targetProduct, setTargetProduct] = useState(null);
  const [ndcOptions, setNdcOptions] = useState([]);
  /* The target's NDCs, kept apart from the source's. They are two different drugs in a product
   * change, and one list cannot answer both which NDC an order is on now and which it is moving
   * to -- loading the target over the source made the Current NDCs filter offer the new drug. */
  const [targetNdcOptions, setTargetNdcOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [isRowsLoading, setIsRowsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [selectedIds, setSelectedIds] = useState([]);
  /* How many a select-all takes. Empty means all of them. Outlives both the drug and the
   * commit -- it is how the operator likes to work, not part of a selection. Its control is
   * hidden for now (SHOW_SELECT_LIMIT in BulkOverride.js) but the wiring stays. */
  const [selectLimit, setSelectLimit] = useState('');
  const [stagedValues, setStagedValues] = useState({});
  const [bulkValue, setBulkValue] = useState('');
  const [bulkQuantity, setBulkQuantity] = useState('');
  /* FILL_LATER_SPLIT only. Lives in its own hook -- see useFillLaterSchedule for why. */
  const [isCommitting, setIsCommitting] = useState(false);
  /* A failed load belongs in the grid, where the rows would have been. A failed commit is an
   * event, not a property of the list, so it goes through the app's error handler instead. */
  const [loadError, setLoadError] = useState(null);

  /* Advanced filters never hit the server. The drug's orders are fetched once and
   * narrowed here, so changing a filter is instant and the counts, the facets and
   * "Select all" all speak for the same set rather than for one page of it. */
  const visibleRows = useMemo(() => {
    const orderId = filters.orderId.trim().toLowerCase();
    const qty = filters.qty.trim();
    const ndc = ndcDigits(filters.ndc);

    return rows.filter((row) => {
      if (ndc && !ndcDigits(row.ndc).includes(ndc)) return false;
      if (qty && String(row.quantity) !== qty) return false;
      if (filters.facility && row.fulfillingPharmacyId !== filters.facility) return false;
      if (filters.partner && samePartner(row.partner) !== samePartner(filters.partner)) {
        return false;
      }
      if (orderId && !String(row.orderId).toLowerCase().includes(orderId)) return false;
      if (filters.from && isoDay(row.createdAt) < filters.from) return false;
      if (filters.to && isoDay(row.createdAt) > filters.to) return false;

      return true;
    });
  }, [rows, filters]);

  const fillLater = useFillLaterSchedule({
    isActive: isFillLaterSplit,
    rows: visibleRows,
    selectedIds,
    stagedValues,
    /* The quantity filter matches exactly, so while it is on every visible row carries this
     * number and the bar can refuse a schedule before it is stamped. */
    dispenseQuantity: filters.qty.trim(),
  });

  /* A product change only lands on the same product when the operator picked the drug it is
   * already on, and only then can matching an NDC and a quantity mean nothing is moving. */
  const isSameProduct = Boolean(
    targetProduct?.productId && targetProduct.productId === product?.productId
  );

  /* The one answer to "will this row be written?", shared by the strikethroughs, the green row,
   * the footer tally, the confirm summary and commit() itself, so none of them can disagree.
   * Review has no current reason on the row to compare against, so it passes none. */
  const isRowChanged = useCallback(
    (row) => {
      /* Unfulfillment has no value to stage, so the selection is the change: a selected row is
       * the row that will be written, which keeps the tally, the summary and commit() in step. */
      if (isUnfulfillment) return selectedIds.includes(row.lineItemId);

      return isStagedChange({
        staged: stagedValues[row.lineItemId],
        ...(isReview ? {} : { currentNdc: row.ndc, currentQuantity: row.quantity }),
        isSplit: isFillLaterSplit,
        isMove: isFillLaterMove,
        isSameProduct,
        isQuantityOnly: isQuantityChange,
      });
    },
    [
      stagedValues,
      isReview,
      isUnfulfillment,
      selectedIds,
      isSameProduct,
      isQuantityChange,
      isFillLaterSplit,
      isFillLaterMove,
    ]
  );

  /* Derived from the rows on screen, not from the keys of stagedValues and not from every fetched
   * row: a filtered-out row is not committed, so the footer tally, the commit summary and what
   * commit() actually writes are all the same set -- the visible one. */
  const stagedIds = useMemo(
    () => visibleRows.filter(isRowChanged).map((row) => row.lineItemId),
    [visibleRows, isRowChanged]
  );

  /* Staged, but filtered out of sight, so deliberately not part of the commit. Surfaced in the
   * footer because those edits are discarded rather than written -- silently dropping them is the
   * one outcome the operator must not be able to walk into. */
  const hiddenStagedCount = useMemo(
    () => rows.filter(isRowChanged).length - stagedIds.length,
    [rows, isRowChanged, stagedIds]
  );
  const partnerFacet = useMemo(() => buildPartnerFacet(rows), [rows]);

  /* isSilent keeps the grid on screen while it refreshes. A post-commit reload is not
   * something the operator asked for, so tearing the rows down to a skeleton reads as
   * though the screen lost their work. */
  const loadRows = useCallback(
    async (loadParams) => {
      const { productId, isSilent } = loadParams;
      if (!isSilent) setIsRowsLoading(true);
      setLoadError(null);

      try {
        const { bulkLabels, count } = await listBulkLabelsApi({
          page: 1,
          limit: MAX_ROWS_PER_DRUG,
          filter: {
            ...standingFilterFor({ isReview, isUnfulfillment, isFillLaterSplit, isFillLaterMove }),
            ...(productId ? { productIds: [productId] } : {}),
          },
        });

        setTotalCount(count || 0);
        setRows(bulkLabels);
      } catch (requestError) {
        setLoadError(true);
      } finally {
        if (!isSilent) setIsRowsLoading(false);
      }
    },
    [isReview, isUnfulfillment, isFillLaterSplit, isFillLaterMove]
  );

  /* Every mode opens on the drug now, review included: it used to load the whole queue on open,
   * which is what listed orders that had already shipped or gone past verification. The drug is
   * asked for first, then fetched together with the standing filter for that mode. */
  useEffect(() => {
    if (!open) return;
    if (!product) return;

    loadRows({ productId: product.productId });
  }, [open, product, loadRows]);

  // the value picker is disabled without a selection, so a value left behind
  // reads oddly next to "No orders selected yet"
  useEffect(() => {
    if (!selectedIds.length) {
      setBulkValue('');
      setBulkQuantity('');
    }
  }, [selectedIds]);

  const searchDrugs = useMemo(
    () =>
      debounce(async (drugName) => {
        setIsDrugLoading(true);

        try {
          setDrugOptions(await listCatalogProductsByDrugNameApi(drugName));
        } finally {
          setIsDrugLoading(false);
        }
      }, DRUG_SEARCH_DEBOUNCE_MS),
    []
  );

  const loadTargetNdcOptions = useCallback(async (productId) => {
    setTargetNdcOptions(productId ? await listCatalogNdcsByProductIdApi(productId) : []);
  }, []);

  const loadNdcOptions = useCallback(async (productId) => {
    setNdcOptions(productId ? await listCatalogNdcsByProductIdApi(productId) : []);
  }, []);

  const resetStaging = () => {
    setSelectedIds([]);
    setStagedValues({});
    setBulkValue('');
    setBulkQuantity('');
    fillLater.clearSchedule();
    setFilters(EMPTY_FILTERS);
  };

  const clearSelection = () => {
    setStagedValues((current) => {
      const next = { ...current };
      selectedIds.forEach((lineItemId) => delete next[lineItemId]);

      return next;
    });
    setSelectedIds([]);
    setBulkValue('');
    setBulkQuantity('');
  };

  /* The drug is the scope in every mode, review included, so changing it means a different list
   * entirely and everything hanging off the old one has to go. */
  const handleProduct = (selected) => {
    setProduct(selected);

    setFilters(EMPTY_FILTERS);
    resetStaging();

    if (!selected) {
      setRows([]);
      setNdcOptions([]);
      setTargetNdcOptions([]);
      return;
    }

    /* Every mode: this is the Current NDCs filter, which describes the orders on screen. Product
     * change skipped it, which is why that filter offered nothing until a target was picked. */
    loadNdcOptions(selected.productId);
  };

  const handleTargetProduct = (selected) => {
    setTargetProduct(selected);
    setStagedValues({});
    setBulkValue('');
    setBulkQuantity('');
    loadTargetNdcOptions(selected?.productId);
  };

  const changeFilter = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const toggleRow = useCallback(
    (lineItemId) =>
      setSelectedIds((current) =>
        current.includes(lineItemId)
          ? current.filter((id) => id !== lineItemId)
          : [...current, lineItemId]
      ),
    []
  );

  /* Takes the ids in the order the grid is showing them, so “first 50” means the 50 at the
   * top of the screen even after the operator sorts a column. */
  const limitIds = useCallback(
    (ids) => {
      const limit = Number(selectLimit);

      return limit > 0 ? ids.slice(0, limit) : ids;
    },
    [selectLimit]
  );

  const toggleAll = useCallback(
    (orderedIds) => {
      const target = limitIds(orderedIds);

      setSelectedIds((current) => (current.length >= target.length ? [] : target));
    },
    [limitIds]
  );

  const changeRowValue = useCallback((rowParams) => {
    const { lineItemId, value } = rowParams;

    setStagedValues((current) => ({ ...current, [lineItemId]: value }));
  }, []);

  const changeRowField = useCallback((rowParams) => {
    const { lineItemId, field, value } = rowParams;

    setStagedValues((current) => ({
      ...current,
      [lineItemId]: { ...(current[lineItemId] || EMPTY_ROW_EDIT), [field]: value },
    }));
  }, []);

  /* Selected AND on screen -- see selectedAndVisible for why Apply must not use the raw selection. */
  const visibleSelectedIds = useMemo(
    () => selectedAndVisible({ selectedIds, visibleRows }),
    [selectedIds, visibleRows]
  );

  const applyToSelection = () =>
    setStagedValues((current) => ({
      ...current,
      ...visibleSelectedIds.reduce(
        (staged, lineItemId) => ({
          ...staged,
          /* Quantity mode stages the quantity field, not bulkValue: its apply strip has no NDC
           * picker, so bulkValue is always '' there and applying would clear every staged row. */
          [lineItemId]: buildBulkStagedValue({
            currentEdit: current[lineItemId],
            bulkValue,
            bulkQuantity,
            bulkSchedule: fillLater.schedule,
            moveDate: fillLater.moveDate,
            isProductChange,
            isQuantityChange,
            isFillLaterSplit,
            isFillLaterMove,
          }),
        }),
        {}
      ),
    }));

  /* What a finished run leaves behind, extracted so the synchronous and the queued paths cannot
   * drift. A queued run reaches this only when it settles: at submit time the staged values are
   * still the record a failed chunk is recovered from, and the drug is still what the grid behind
   * the progress dialog is showing. */
  const settleAfterCommit = () => {
    /* Whatever the operation, the staging and the selection are spent. Failed rows are counted
     * for the caller's snackbar but not kept staged, so the count is the only record the
     * operator gets of which run had refusals. */
    setSelectedIds([]);
    setStagedValues({});
    setBulkValue('');
    setBulkQuantity('');
    fillLater.clearSchedule();

    /* Every operation is scoped to a drug, so every run is done with its drug: drop it, drop the
     * filters, drop the grid, and the operator lands back on the picker to choose the next one. */
    setFilters(EMPTY_FILTERS);
    setProduct(null);
    /* The target goes with the source. Left behind, it put the finished run's "new drug" in
     * the apply bar of the next one -- beside a NEW NDC list that was cleared with it, since
     * those options are loaded from the target. Selecting orders and applying from there
     * staged them against a target chosen for orders no longer on screen. */
    setTargetProduct(null);
    setNdcOptions([]);
    setTargetNdcOptions([]);
    setRows([]);
  };

  /* The items a commit writes, in grid order. One builder for both the single-request and the
   * chunked path, so they can never drift on what a staged row means. */
  const buildCommitItems = () =>
    commitItemsFor({
      rows,
      stagedIds,
      stagedValues,
      targetProductId: targetProduct?.productId,
      isUnfulfillment,
      isReview,
      isFillLaterSplit,
      isFillLaterMove,
      isQuantityChange,
      isProductChange,
    });

  const dropStagedFor = (lineItemIds) =>
    setStagedValues((current) => {
      const next = { ...current };
      lineItemIds.forEach((lineItemId) => delete next[lineItemId]);

      return next;
    });

  const submitChunk = (chunk) =>
    bulkOverrideLineItemsApi({
      operationType: isFillLaterMove ? bulkOverrideOperation.FILL_LATER_MOVE : operation,
      productId: product?.productId,
      items: chunk,
    });

  const commitSingle = async (items) => {
    try {
      const response = await submitChunk(items);

      /* Above the server's threshold (50) the work is queued: `results` is empty and the real
       * outcome arrives over websocket. Hand the run to the progress tracker and return early —
       * reading `results` here would report every queued run as "0 updated", and clearing the
       * staged values now would lose the rows we still need if a chunk fails. The selection does
       * go, though: it is not what a failed chunk is recovered from, and leaving it behind lets
       * the operator commit work the server has already accepted. */
      if (response?.status === 'QUEUED') {
        const accepted = response.accepted ?? items.length;

        onQueued?.({
          batches: [{ batchId: response.batchId, accepted }],
          accepted,
          sync: { succeeded: 0, failed: 0, total: 0 },
          /* The ids this run submitted. The label-regeneration tracker matches per-label socket
           * events on these — the queued response itself carries no ids, only counts. */
          lineItemIds: items.map((item) => item.lineItemId),
        });
        setSelectedIds([]);

        return { queued: true, accepted };
      }

      const committed = response?.results ?? [];

      /* Every operation leaves the list stale, so all three refetch. An ndc update changes the
       * row it wrote to; a product change moves the order off this productId entirely; a review
       * commit takes it out of taggedForReview: false. In the last two the rows should leave the
       * list, and without this they sit there looking untouched and can be committed again.
       * Refetch rather than patch: the server strips hyphens and resolves the ndc against
       * inventory, so what it stored is not necessarily what we sent. */
      const failed = new Set(
        committed.filter(({ success }) => !success).map(({ lineItemId }) => lineItemId)
      );

      settleAfterCommit();

      return { failedCount: failed.size, total: committed.length };
    } catch (requestError) {
      dispatch(handleError({ error: requestError, heading: 'Bulk update' }));

      return null;
    }
  };

  /* A chunked run that died at chunk k. Chunks 1..k-1 are the server's now: their staging is
   * spent and must not be committable again, so it is dropped. What was never sent stays staged
   * AND selected, so the operator can retry exactly the remainder — but whether that retry is
   * safe depends on HOW the send failed. `isDefinite` says the server refused the chunk; without
   * it the chunk may be running server-side (core queues before the response travels back), and
   * the caller's copy has to warn the operator to refresh before retrying. */
  const failChunkedCommit = ({ error, batches, sync, submittedIds, allIds }) => {
    dropStagedFor(submittedIds);

    const submitted = new Set(submittedIds);
    const remainingIds = allIds.filter((lineItemId) => !submitted.has(lineItemId));

    setSelectedIds(remainingIds);

    /* Nothing landed at all — an ordinary failed commit, not a partial one. */
    if (batches.length === 0 && sync.total === 0) {
      dispatch(handleError({ error, heading: 'Bulk update' }));

      return null;
    }

    /* The batches that DID queue are running: hand them to the tracker or their completion
     * events land on the background snackbar as a run nobody started. */
    if (batches.length > 0) {
      onQueued?.({
        batches,
        accepted: batches.reduce((sum, { accepted }) => sum + accepted, 0),
        sync,
        lineItemIds: submittedIds,
        isPartial: true,
      });
    }

    return {
      partial: true,
      queued: batches.length > 0,
      submittedCount: submittedIds.length,
      unsubmittedCount: remainingIds.length,
      isDefinite: isDefiniteRejection(error),
    };
  };

  const commitChunked = async (chunks) => {
    const allIds = chunks.flat().map((item) => item.lineItemId);

    onChunkedCommitStart?.({ lineItemIds: allIds });

    const { batches, sync, submittedIds, error } = await submitChunksSequentially({
      chunks,
      submit: submitChunk,
      onChunkAccepted: onBatchAccepted,
    });

    if (error) return failChunkedCommit({ error, batches, sync, submittedIds, allIds });

    /* Every chunk ran synchronously — the async kill switch is off server-side, or the threshold
     * was raised past the chunk size. Everything is already written, so this is the small-run
     * sync path at scale: settle and report the counts. */
    if (batches.length === 0) {
      settleAfterCommit();

      return { failedCount: sync.failed, total: sync.total };
    }

    const accepted = batches.reduce((sum, batch) => sum + batch.accepted, 0);

    onQueued?.({ batches, accepted, sync, lineItemIds: submittedIds });
    setSelectedIds([]);

    return { queued: true, accepted: accepted + sync.total };
  };

  const commit = async () => {
    setIsCommitting(true);

    try {
      const items = buildCommitItems();
      const chunks = buildCommitChunks(items);

      /* One request when it fits — identical to the pre-chunking behaviour, error path included.
       * Splitting only ever happens for runs that would otherwise 413 on the platform's 100kb
       * request-body limit. */
      if (chunks.length === 1) return await commitSingle(items);

      return await commitChunked(chunks);
    } finally {
      setIsCommitting(false);
    }
  };

  return {
    isReview,
    isProductChange,
    isUnfulfillment,
    isQuantityChange,
    product,
    targetProduct,
    drugOptions,
    isDrugLoading,
    ndcOptions,
    targetNdcOptions,
    rows,
    visibleRows,
    partnerFacet,
    isRowsLoading,
    totalCount,
    filters,
    selectedIds,
    visibleSelectedIds,
    stagedValues,
    stagedIds,
    hiddenStagedCount,
    isRowChanged,
    bulkValue,
    bulkQuantity,
    isCommitting,
    loadError,
    retry: () => loadRows({ productId: product?.productId }),
    settleAfterCommit,
    setBulkValue,
    setBulkQuantity,
    ...fillLater,
    setSelectedIds,
    searchDrugs,
    resetStaging,
    clearSelection,
    handleProduct,
    handleTargetProduct,
    changeFilter,
    toggleRow,
    toggleAll,
    limitIds,
    selectLimit,
    setSelectLimit,
    changeRowValue,
    changeRowField,
    isQtyFilterActive: Boolean(filters.qty.trim()),
    /* The value, not just the flag: the filter is an exact match, so while it is on this is the
     * dispense quantity every visible row carries, and the apply bar validates against it. */
    qtyFilterValue: filters.qty.trim(),
    applyToSelection,
    commit,
  };
};

export default useBulkOverride;
